// Copyright (C) 2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import Popover from 'antd/lib/popover';
import Icon from '@ant-design/icons';

import { Canvas } from 'cvat-canvas-wrapper';
import { ActiveControl } from 'reducers';
import CVATTooltip from 'components/common/cvat-tooltip';
import { t } from 'utils/i18n';
import withVisibilityHandling from './handle-popover-visibility';
import { CropIcon } from 'icons';
import GlobalHotKeys, { KeyMapItem } from 'utils/mousetrap-react';

export interface Props {
    canvasInstance: Canvas;
    activeControl: ActiveControl;
    updateActiveControl(activeControl: ActiveControl): void;
    shortcuts: {
        CANCEL: {
            details: KeyMapItem;
            displayValue: string;
        };
    }
}

function CropControl(props: Props): JSX.Element {
    const {
        canvasInstance,
        activeControl,
        updateActiveControl,
        shortcuts
    } = props;

    const isCropActive = activeControl === ActiveControl.CROP;

    const toggleCrop = (): void => {
        if (isCropActive) {
            canvasInstance.crop({ enabled: false });
            updateActiveControl(ActiveControl.CURSOR);
        } else {
            canvasInstance.crop({ enabled: true });
            updateActiveControl(ActiveControl.CROP);
        }
    };

    const cancel = (event?: KeyboardEvent): void => {
        if (event) event.preventDefault();
        if (activeControl === ActiveControl.CROP) {
            toggleCrop();
        }
    };

    const iconProps = {
        className: isCropActive ?
            'cvat-crop-control cvat-active-canvas-control' :
            'cvat-crop-control',
        onClick: toggleCrop,
    };

    return (
        <>
            <GlobalHotKeys
                keyMap={{ CANCEL: shortcuts.CANCEL.details }}
                handlers={{ CANCEL: cancel }}
            />
            <CVATTooltip title={t('Crop image')} placement='right'>
                <Icon {...iconProps} component={CropIcon} />
            </CVATTooltip>
        </>
    );
}

export default React.memo(CropControl);